/**
 * This tab's session: obtaining one, and letting go of it.
 *
 * <p>THE CLIENT NEVER MINTS THIS VALUE. Until #366 it did - `SSEClient`'s constructor generated a
 * GUID - and a value the caller chooses cannot be the thing lock ownership is judged by: anyone
 * could have claimed anyone's. The server now issues it as a `Users.UserTabSession` row and the
 * client's whole job is to remember which one it was given (D-114).</p>
 *
 * <p>Three ways a tab gets one, and the third is the one most tabs use:</p>
 * <ol>
 *   <li>It signed in, and the login response carried it.</li>
 *   <li>It switched account, and the switch response carried a fresh one.</li>
 *   <li>It did NEITHER. The auth cookie is browser-wide, so only the first tab ever signs in -
 *       every tab opened afterwards is already authenticated and never touches the login path. It
 *       has to ask, and that is what `ensureTabSessionId` does.</li>
 * </ol>
 *
 * <p>The third also covers every tab that arrived with an id and gave it up at load: a duplicated
 * or reopened tab, and any full navigation into the app that is not a reload, since only a reload
 * keeps the id it finds (D-136, the arrival rule in `utility/session-storage-keys.ts`).</p>
 *
 * <p>The synchronous read/write half lives in `utility/session-storage-keys.ts` and imports
 * nothing, because `ApiFactory` reads the value on every request while `sse-client` imports
 * `ApiFactory`; issuing needs the api layer, so only this half depends on it.</p>
 */

import { ApiFactory } from '../api/agwasuite-api-factory';
import { ApiTabSession } from '../api/core/agwasuite-api-tab-session';
import { isUnanswered } from '../api/unanswered-request';
import {
  adoptIssuedTabSession,
  clearTabSessionId,
  readTabSessionId,
  tabSessionArrival,
  writeTabSessionId,
} from '../utility/session-storage-keys';

export { adoptIssuedTabSession, clearTabSessionId, readTabSessionId };

/**
 * The single in-flight issue request.
 *
 * <p>THIS IS THE CLIENT'S HALF OF A DELIBERATE SERVER-SIDE NON-IDEMPOTENCY. Every call to the
 * endpoint issues a NEW session, because the server cannot tell a retried request from a genuine
 * second tab. Two concurrent callers here - the SSE connect and a first lock acquire racing on
 * boot - would otherwise take two sessions, and the tab would hold locks under one while sending
 * the other. Sharing the promise makes the tab ask exactly once.</p>
 */
let issueInFlight: Promise<string | undefined> | null = null;

/**
 * Whether the most recent issue request got no answer from the server (D-144). `ensureTabSessionId`
 * returns undefined for every failure, so a caller that must tell "the server refused" from "the
 * server never answered" (the lock store, to present nothing for the second) asks this after it.
 */
let lastIssueUnanswered = false;

/** Whether the last attempt to issue this tab a session went unanswered. */
export function lastTabSessionIssueWentUnanswered(): boolean {
  return lastIssueUnanswered;
}

/**
 * Returns this tab's session, asking the server for one if it has none.
 *
 * <p>Returns undefined rather than throwing when issuance fails. The caller is typically
 * establishing the event stream or releasing a lock, and neither can act on the failure: without a
 * session the tab simply holds no locks, which is a lesser state than being broken. Anything that
 * genuinely requires one is refused by the server, which is where that decision belongs.</p>
 */
export async function ensureTabSessionId(): Promise<string | undefined> {
  // A reloaded tab may still be deciding whether the id it found is its own (D-136). Until that
  // settles `readTabSessionId` reads nothing, and asking the server now would issue a second session
  // to a tab about to keep its first.
  await tabSessionArrival();
  const existing = readTabSessionId();
  if (existing) {
    return existing;
  }

  if (issueInFlight) {
    // JOINED SOMEBODY ELSE'S ATTEMPT. Sharing it is what stops a tab taking two sessions when the
    // boot issuance and a first lock acquire overlap.
    const shared = await issueInFlight;
    if (shared) {
      return shared;
    }

    // It failed, and a joined caller must NOT inherit that failure - our need is still unmet and
    // unattempted. This is the @LOCK-016 race: the boot attempt is aborted, the acquire arrives
    // while it is still in flight, joins it, and is handed the abort's undefined, so the tab
    // reports it cannot start an editing session when a fresh request would have worked. It
    // reproduced as a flaky journey, passing only when the acquire landed after boot had settled.
    const settled = readTabSessionId();
    if (settled) {
      return settled;
    }
    return (issueInFlight ??= issueTabSession());
  }

  // OUR OWN ATTEMPT, AND EXACTLY ONE. Deliberately no retry here: boot is a caller like any other,
  // and a tab whose issuance fails at load is meant to be left without a session until something
  // actually needs one. Retrying here instead would heal the tab invisibly at boot, which is both
  // the wrong place - the rule is that whatever needs a session ensures one at the point of need -
  // and unobservable, since it makes the failure the @LOCK-016 journey injects disappear.
  return (issueInFlight = issueTabSession());
}

async function issueTabSession(): Promise<string | undefined> {
  try {
    const api = ApiFactory.authenticated(ApiTabSession);
    const response = await api.issue();
    lastIssueUnanswered = isUnanswered(response);
    const issued = response.success ? response.data?.tabSessionId : undefined;
    if (issued) {
      writeTabSessionId(issued);
    }
    return issued;
  } catch {
    // Thrown, not answered-or-unanswered: nothing is known about the connection.
    lastIssueUnanswered = false;
    return undefined;
  } finally {
    issueInFlight = null;
  }
}
